import apiClient, { type ApiError } from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from '@/lib/api/config'
import type {
  Invitee,
  InviteeListResponse,
  InviteeFilters,
  CreateInviteeRequest,
  CreateInviteeResponse,
  InviteeLookupResult,
  Property,
  Organization,
  Company,
} from '../types/invitee'
import { validateInvitationUpdateData } from '../utils/inviteeFormatters'

/**
 * Get invitee list with filters
 * @param filters - Filter parameters
 * @returns Invitee list response with pagination
 * @throws ApiError if request fails
 */
export async function getInviteeList(filters: InviteeFilters): Promise<InviteeListResponse> {
  try {
    const params = new URLSearchParams()
    
    // Pagination
    if (filters.page) params.append('page', String(filters.page))
    if (filters.perPage) params.append('perPage', String(filters.perPage))
    
    // Sorting
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.orderBy) params.append('orderBy', filters.orderBy)
    
    // Filters
    if (filters.q) params.append('q', filters.q)
    if (filters.org_id) params.append('org_id', filters.org_id)
    if (filters.company_id) params.append('company_id', filters.company_id)
    if (filters.property_id) params.append('property_id', filters.property_id)
    if (filters.fromDt) params.append('fromDt', filters.fromDt)
    if (filters.toDt) params.append('toDt', filters.toDt)
    
    // Invite status (array)
    if (filters.inviteStatus && filters.inviteStatus.length > 0) {
      filters.inviteStatus.forEach(status => {
        params.append('inviteStatus[]', status)
      })
    }
    
    const response = await apiClient.get<InviteeListResponse>(
      `${API_ENDPOINTS.INVITE_LIST}?${params.toString()}`
    )
    
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch invitee list.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Create new invitee
 * @param data - Invitee data
 * @returns Created invitee response
 * @throws ApiError if request fails
 */
export async function createInvitee(data: CreateInviteeRequest): Promise<CreateInviteeResponse> {
  try {
    const response = await apiClient.post<CreateInviteeResponse>(
      API_ENDPOINTS.INVITE_CREATE,
      data
    )
    
    return response.data
  } catch (error) {
    const apiError = error as ApiError & { data?: Record<string, unknown> }
    // Preserve validation errors for proper error formatting (like Angular)
    throw {
      message: extractErrorMessage(error, 'Failed to create invitee.'),
      status: apiError.status,
      errors: apiError.errors,
      // Preserve full response data for formatting (like Angular's error.error)
      error: apiError.data || apiError.errors,
    }
  }
}

/**
 * Send invitation
 * @param inviteCode - Invite code
 * @returns Success message
 * @throws ApiError if request fails
 */
export async function sendInvitation(inviteCode: string): Promise<string> {
  try {
    const response = await apiClient.post<string>(
      API_ENDPOINTS.INVITE_SEND(inviteCode)
    )
    
    return typeof response.data === 'string' ? response.data : 'Invitation sent successfully'
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to send invitation.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Resend invitation
 * @param inviteCode - Invite code
 * @returns Success message
 * @throws ApiError if request fails
 */
export async function resendInvitation(inviteCode: string): Promise<string> {
  try {
    const response = await apiClient.get<string>(
      API_ENDPOINTS.INVITE_RESEND(inviteCode)
    )
    
    return typeof response.data === 'string' ? response.data : 'Invitation resent successfully'
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to resend invitation.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Lookup invitee (duplicate detection)
 * @param params - Lookup parameters (first_name, last_name, email?, mobile_no?)
 * @returns Matched invitee data or null
 * @throws ApiError if request fails
 */
export async function lookupInvitee(params: {
  first_name?: string
  last_name?: string
  email?: string
  mobile_no?: string
  searchByEmail?: boolean // When true, only send email and mobile_no (like Angular)
}): Promise<InviteeLookupResult | null> {
  try {
    const queryParams = new URLSearchParams()
    
    // Angular behavior: when searchByEmail is true, only send email and mobile_no
    // When false, send first_name and last_name
    if (params.searchByEmail) {
      // Email/mobile lookup - only send email and mobile_no (like Angular checkForInviteEmail)
      if (params.email) queryParams.append('email', params.email)
      if (params.mobile_no) {
        // Clean phone number (remove non-numeric characters) before sending to API
        const cleanedMobile = params.mobile_no.replace(/\D/g, '')
        queryParams.append('mobile_no', cleanedMobile)
      }
    } else {
      // Name lookup - send first_name and last_name (like Angular checkForInvite)
      if (params.first_name) queryParams.append('first_name', params.first_name)
      if (params.last_name) queryParams.append('last_name', params.last_name)
    }
    
    // Don't make request if no params
    if (queryParams.toString() === '') {
      return null
    }
    
    const response = await apiClient.get<InviteeLookupResult | null>(
      `${API_ENDPOINTS.INVITE_LOOKUP}?${queryParams.toString()}`,
    )
    
    return response.data || null
  } catch (error) {
    // If not found, return null instead of throwing
    if ((error as ApiError).status === 404) {
      return null
    }
    throw {
      message: extractErrorMessage(error, 'Failed to lookup invitee.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Update invitee
 * @param inviteCode - Invite code
 * @param data - Updated invitee data
 * @returns Updated invitee
 * @throws ApiError if request fails
 */
export async function updateInvitee(
  inviteCode: string,
  data: Partial<CreateInviteeRequest>
): Promise<Invitee> {
  try {
    const response = await apiClient.put<Invitee>(
      API_ENDPOINTS.INVITE_EDIT(inviteCode),
      data
    )
    
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update invitee.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Add co-applicant to existing invite
 * @param inviteCode - Invite code
 * @param data - Co-applicant data
 * @returns Updated invitee
 * @throws ApiError if request fails
 */
export async function addCoApplicant(
  inviteCode: string,
  data: {
    first_name: string
    last_name: string
    email: string
    mobile_no: string
    relationship_type: string
    living_unit: '0' | '1'
    is_approved_require: '0' | '1'
    income_verified: '0' | '1'
    income?: string
  }
): Promise<Invitee> {
  try {
    const response = await apiClient.post<Invitee>(
      API_ENDPOINTS.INVITE_ADD_COAPPLICANT(inviteCode),
      data
    )
    
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to add co-applicant.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Search properties
 * @param searchText - Search text (property name, city, state)
 * @param companyId - Optional company ID filter
 * @param orgId - Optional organization ID filter
 * @param listed - Filter by listed status (default: '1' to only get active properties)
 * @returns Array of properties
 * @throws ApiError if request fails
 */
export async function getProperties(
  searchText: string = '',
  companyId?: string,
  orgId?: string,
  listed: '0' | '1' = '1'
): Promise<Property[]> {
  try {
    const params = new URLSearchParams()
    if (searchText) params.append('q', searchText)
    if (companyId) params.append('company_id', companyId)
    if (orgId) params.append('org_id', orgId)
    params.append('listed', listed) // Only get active properties like Angular
    
    const response = await apiClient.get<Property[]>(
      `${API_ENDPOINTS.ALL_PROPERTY}?${params.toString()}`
    )
    
    // Handle array response
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    // Handle wrapped response
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const wrappedData = (response.data as { data: Property[] }).data
      return Array.isArray(wrappedData) ? wrappedData : []
    }
    
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch properties.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Get property details
 * @param propertyId - Property ID
 * @returns Property details
 * @throws ApiError if request fails
 */
export async function getPropertyDetails(propertyId: string): Promise<Property> {
  try {
    const response = await apiClient.get<Property>(
      API_ENDPOINTS.PROPERTY_DETAILS(propertyId)
    )
    
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch property details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Search organizations
 * @param searchText - Search text
 * @returns Array of organizations
 * @throws ApiError if request fails
 */
export async function getOrganizations(searchText: string = ''): Promise<Organization[]> {
  try {
    const params = new URLSearchParams()
    if (searchText) params.append('q', searchText)
    
    const response = await apiClient.get<Organization[]>(
      `${API_ENDPOINTS.ALL_ORGANIZATIONS}?${params.toString()}`
    )
    
    // Handle array response
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    // Handle wrapped response
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const wrappedData = (response.data as { data: Organization[] }).data
      return Array.isArray(wrappedData) ? wrappedData : []
    }
    
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch organizations.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Search companies
 * @param searchText - Search text
 * @param orgId - Optional organization ID filter
 * @returns Array of companies
 * @throws ApiError if request fails
 */
export async function getCompanies(
  searchText: string = '',
  orgId?: string
): Promise<Company[]> {
  try {
    const params = new URLSearchParams()
    if (searchText) params.append('q', searchText)
    if (orgId) params.append('org_id', orgId)
    
    const response = await apiClient.get<Company[]>(
      `${API_ENDPOINTS.ALL_COMPANIES}?${params.toString()}`
    )
    
    // Handle array response
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    // Handle wrapped response
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const wrappedData = (response.data as { data: Company[] }).data
      return Array.isArray(wrappedData) ? wrappedData : []
    }
    
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to fetch companies.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Add invite log (for popid tracking)
 * @param popid - Popup ID
 * @returns Success response
 * @throws ApiError if request fails
 */
export async function addInviteLog(popid: string): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.INVITE_LOG, { popid })
  } catch (error) {
    // Log error but don't throw - this is optional tracking
    console.error('Failed to add invite log:', error)
  }
}

/**
 * Update invitation / add co-applicant via Angular parity endpoint
 * POST /api/update-invitation/:inviteCode
 */
export async function updateInvitation(inviteCode: string, data: Record<string, any>): Promise<string> {
  try {
    const payload = validateInvitationUpdateData(data)
    const response = await apiClient.post<string>(API_ENDPOINTS.INVITE_UPDATE_INVITATION(inviteCode), payload)
    return typeof response.data === 'string' ? response.data : 'Updated successfully'
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update invitation.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
